import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  DatePicker as MuiDatePicker,
  DateTimePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import esLocale from "date-fns/locale/es";
import { makeStyles } from "@material-ui/core/styles";
import { FONT_FAMILY, BORDER_RADIUS } from "../styles/variables";
import ThemeContext from "../utils/theme";

const INPUT_BORDER_COLOR = (opacity = 1) => `rgba(0, 15, 25, ${opacity})`;

const useStyles = makeStyles({
  root: {
    display: "flex",
    fontFamily: FONT_FAMILY,
    marginTop: (props) => props.spacingXl,
    "& label.Mui-focused": {
      color: (props) => props.colorTextBase,
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "red",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: BORDER_RADIUS,
      "& fieldset": {
        borderColor: INPUT_BORDER_COLOR(0.1),
      },
      "&:hover fieldset": {
        borderColor: INPUT_BORDER_COLOR(0.3),
      },
      "&.Mui-focused fieldset": {
        borderWidth: "1px",
        borderColor: INPUT_BORDER_COLOR(0.6),
      },
      "&.Mui-disabled": {
        color: "rgba(0, 0, 0, 0.1)",
        "& fielset": {
          borderColor: "rgba(0, 0, 0, .1)",
        },
      },
    },
  },
});

export default function DatePicker({ id, onChange, ...restProps }) {
  const { colorTextBase, spacingXl } = React.useContext(ThemeContext);

  const classes = useStyles({ colorTextBase, spacingXl });
  const {
    type,
    label,
    helperText,
    error,
    minDate: pMinDate,
    maxDate: pMaxDate,
  } = restProps;

  const [value, changeValue] = useState(null);
  const { minDate, maxDate } = useMemo(() => {
    const datefns = new DateFnsUtils();

    return {
      minDate: pMinDate && datefns.isValid(pMinDate) && new Date(pMinDate),
      maxDate: pMaxDate && datefns.isValid(pMaxDate) && new Date(pMaxDate),
    };
  }, [pMinDate, pMaxDate]);

  const { TypeDatePicker, formatValue } = useMemo(
    function () {
      if (type === "datetime-local") {
        return {
          TypeDatePicker: DateTimePicker,
          formatValue: "dd/MM/yyyy HH:MM",
        };
      }

      return {
        TypeDatePicker: MuiDatePicker,
        formatValue: "dd/MM/yyyy",
      };
    },
    [type],
  );

  const handleChange = (newValue) => {
    changeValue(newValue);

    if (onChange) {
      onChange({
        target: {
          name: id,
          value: newValue,
        },
      });
    }
  };

  const handleOpen = () => {
    if (!value) {
      const today = new Date();

      if (minDate && maxDate) {
        if (minDate.getTime() > today.getTime()) {
          handleChange(minDate);
        } else if (maxDate.getTime() < today.getTime()) {
          handleChange(maxDate);
        } else {
          handleChange(today);
        }
      } else if (minDate) {
        if (minDate.getTime() > today.getTime()) {
          handleChange(minDate);
        } else {
          handleChange(today);
        }
      } else if (maxDate) {
        if (maxDate.getTime() < today.getTime()) {
          handleChange(maxDate);
        } else {
          handleChange(today);
        }
      } else {
        handleChange(today);
      }
    }
  };

  return (
    <>
      <MuiPickersUtilsProvider utils={DateFnsUtils} locale={esLocale}>
        <TypeDatePicker
          autoOk
          className={classes.root}
          label={label}
          value={value}
          error={error}
          helperText={helperText}
          onChange={handleChange}
          onOpen={handleOpen}
          minDate={minDate}
          maxDate={maxDate}
          variant="dialog"
          orientation="portrait"
          inputVariant="outlined"
          format={formatValue}
          ampm={false}
        />
      </MuiPickersUtilsProvider>
    </>
  );
}

DatePicker.propTypes = {
  id: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};
